/** Clients — the record every other thing in the app hangs off.
 *
 *  DEFECT, found 2026-07-27: this store existed in the schema, carried an index
 *  and was written into every backup, and no code path ever read or wrote it.
 *  `Job.clientId` was declared and never set. The name printed on a proposal an
 *  HOA board signs came from parsing the job's notes for the exact string
 *  "Client: " — so "client:" or "Client -" printed a blank name onto a document
 *  that cannot be edited after issue.
 *
 *  Forever Green maintains 10-30 properties under contract. Without a client
 *  record there is no way to ask "what do we do for this association", which is
 *  the question a maintenance business is actually built around.
 */
import { useEffect, useState } from 'preact/hooks';
import { createRecord, getDb, listLive, putRecord, softDelete } from '../../core/db';
import { jobsSig, refreshCore, toast } from '../state';
import { Empty } from '../bits';
import type { Client } from '../../core/types';

const blank = () => ({ name: '', company: '', phone: '', email: '', address: '', notes: '' });

export function ClientsView() {
  const [clients, setClients] = useState<Client[]>([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<Client | null>(null);
  const [draft, setDraft] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<Client | null>(null);

  async function load() { setClients(await listLive('clients')); }
  useEffect(() => { void load(); }, []);

  const shown = clients.filter((c) => {
    if (!q) return true;
    const hay = `${c.name} ${c.company ?? ''} ${c.email ?? ''} ${c.phone ?? ''} ${c.address ?? ''}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }).sort((a, b) => (a.company || a.name).localeCompare(b.company || b.name));

  const jobCount = (id: string) => jobsSig.value.filter((j) => j.clientId === id).length;

  function startNew() { setEditing(null); setDraft(blank()); }
  function startEdit(c: Client) {
    setEditing(c);
    setDraft({
      name: c.name, company: c.company ?? '', phone: c.phone ?? '',
      email: c.email ?? '', address: c.address ?? '', notes: c.notes ?? '',
    });
  }

  async function save() {
    const name = draft.name.trim();
    const company = draft.company.trim();
    if (!name && !company) { toast('A client needs a name, or the association it is.'); return; }
    setBusy(true);
    try {
      const body = {
        name: name || company, company: company || undefined,
        phone: draft.phone.trim() || undefined, email: draft.email.trim() || undefined,
        address: draft.address.trim() || undefined, notes: draft.notes.trim() || undefined,
      };
      if (editing) await putRecord('clients', { ...editing, ...body });
      else await createRecord('clients', body);
      // refreshCore fills clientsSig, which is what the job's client picker reads.
      // Without it a client added here is invisible to every job until a reload.
      await Promise.all([load(), refreshCore()]);
      setDraft(blank()); setEditing(null);
      toast(editing ? 'Client updated.' : `${body.company || body.name} added.`);
    } finally { setBusy(false); }
  }

  /** A client with jobs against it is not removed — that would orphan documents a
   *  board has already signed. The count is shown and the button refuses. */
  async function remove(c: Client) {
    setBusy(true);
    try {
      await softDelete('clients', c.id);
      await Promise.all([load(), refreshCore()]);
      setConfirming(null);
      toast(`${c.company || c.name} removed. Nothing was destroyed.`);
    } finally { setBusy(false); }
  }

  /** One-time rescue for jobs written before clients existed: the old code stashed
   *  the client in the job's notes. Rather than lose it, offer to turn each
   *  distinct name into a real record and attach the jobs to it. */
  async function adoptFromNotes() {
    setBusy(true);
    try {
      const db = await getDb();
      const byName = new Map<string, string[]>();
      for (const j of jobsSig.value) {
        if (j.clientId) continue;
        const line = (j.notes ?? '').split('\n').find((l) => /^\s*client\s*[:\-]/i.test(l));
        const nm = line ? line.replace(/^\s*client\s*[:\-]\s*/i, '').trim() : '';
        if (!nm) continue;
        byName.set(nm, [...(byName.get(nm) ?? []), j.id]);
      }
      if (!byName.size) { toast('No client names found in job notes.'); return; }
      let made = 0, linked = 0;
      for (const [nm, jobIds] of byName) {
        const existing = (await listLive('clients')).find(
          (c) => (c.company || c.name).toLowerCase() === nm.toLowerCase());
        const client = existing ?? await createRecord('clients', { name: nm, company: nm });
        if (!existing) made++;
        for (const id of jobIds) {
          const job = await db.get('jobs', id);
          if (job) { await putRecord('jobs', { ...job, clientId: client.id }); linked++; }
        }
      }
      await load(); await refreshCore();
      toast(`${made} client${made === 1 ? '' : 's'} created, ${linked} job${linked === 1 ? '' : 's'} attached.`);
    } finally { setBusy(false); }
  }

  const unattached = jobsSig.value.filter(
    (j) => !j.clientId && /^\s*client\s*[:\-]/im.test(j.notes ?? '')).length;

  return (
    <div>
      <div class="pagehead">
        <h1>Clients</h1>
        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
          <input placeholder="Search clients" value={q} aria-label="Search clients"
            onInput={(e) => setQ((e.target as HTMLInputElement).value)} style="min-width:220px;" />
          <button class="btn btn-primary" onClick={startNew}>New client</button>
        </div>
      </div>

      {unattached > 0 && (
        <div class="banner banner-info" role="status">
          <div>
            {unattached} job{unattached === 1 ? ' has a client name' : 's have client names'} written
            into notes from before clients existed. They can be turned into real client records without
            losing anything.
          </div>
          <button class="btn btn-small btn-ghost" disabled={busy} onClick={() => void adoptFromNotes()}>
            Bring them across
          </button>
        </div>
      )}

      <div class="card" style="margin-bottom:16px;">
        <h2 style="font-size:17px;margin-bottom:10px;">{editing ? `Edit ${editing.company || editing.name}` : 'Add a client'}</h2>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;">
          <div><label>Association or company</label>
            <input aria-label="Client company" value={draft.company} style="width:100%;"
              placeholder="Greenway Parc Three HOA"
              onInput={(e) => setDraft({ ...draft, company: (e.target as HTMLInputElement).value })} /></div>
          <div><label>Contact name</label>
            <input aria-label="Client contact name" value={draft.name} style="width:100%;"
              placeholder="Board president, property manager…"
              onInput={(e) => setDraft({ ...draft, name: (e.target as HTMLInputElement).value })} /></div>
          <div><label>Phone</label>
            <input aria-label="Client phone" value={draft.phone} style="width:100%;"
              onInput={(e) => setDraft({ ...draft, phone: (e.target as HTMLInputElement).value })} /></div>
          <div><label>Email</label>
            <input aria-label="Client email" value={draft.email} style="width:100%;"
              onInput={(e) => setDraft({ ...draft, email: (e.target as HTMLInputElement).value })} /></div>
          <div style="grid-column:1/-1;"><label>Billing address</label>
            <input aria-label="Client billing address" value={draft.address} style="width:100%;"
              onInput={(e) => setDraft({ ...draft, address: (e.target as HTMLInputElement).value })} /></div>
          <div style="grid-column:1/-1;"><label>Notes</label>
            <textarea aria-label="Client notes" value={draft.notes} style="width:100%;min-height:60px;"
              onInput={(e) => setDraft({ ...draft, notes: (e.target as HTMLTextAreaElement).value })} /></div>
        </div>
        <p class="small muted" style="margin:8px 0 10px;">
          The association name is what prints on a proposal. The contact is who you call.
        </p>
        <div style="display:flex;gap:8px;">
          <button class="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {editing ? 'Save changes' : 'Add client'}
          </button>
          {editing && <button class="btn btn-ghost" onClick={startNew}>Cancel</button>}
        </div>
      </div>

      {shown.length === 0 ? (
        <Empty>
          {clients.length === 0
            ? <span>No clients yet. Add the association you bid for — every job, proposal and contract hangs off it.</span>
            : <span>Nothing matches that search.</span>}
        </Empty>
      ) : (
        <table class="data clientstable">
          <thead><tr><th>Association</th><th>Contact</th><th>Phone</th><th>Email</th><th class="num">Jobs</th><th></th></tr></thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.id}>
                <td style="font-weight:600;">{c.company || c.name}</td>
                {/* save() copies the company into name when no contact was given,
                    because name is required. Showing it twice reads as a contact
                    called "Greenway Parc Three HOA", which is not a person. */}
                <td class="muted">{c.company && c.name && c.name !== c.company ? c.name : '—'}</td>
                <td class="muted">{c.phone || '—'}</td>
                <td class="muted">{c.email || '—'}</td>
                <td class="num">{jobCount(c.id)}</td>
                <td style="white-space:nowrap;">
                  <button class="btn btn-small btn-ghost" aria-label={`Edit ${c.company || c.name}`}
                    onClick={() => startEdit(c)}>Edit</button>{' '}
                  <button class="btn btn-small btn-ghost" disabled={busy}
                    aria-label={`Remove client ${c.company || c.name}`}
                    onClick={() => setConfirming(c)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirming && (
        <div class="modal-backdrop" role="dialog" aria-modal="true" aria-label="Remove client">
          <div class="card" style="max-width:460px;">
            <h2 style="margin-bottom:8px;">Remove {confirming.company || confirming.name}?</h2>
            {jobCount(confirming.id) > 0 ? (
              <p style="margin-bottom:14px;">
                This client has <b>{jobCount(confirming.id)}</b> job{jobCount(confirming.id) === 1 ? '' : 's'} against it.
                Removing the client would leave those jobs — and any proposal already issued — pointing at nothing.
                Move or remove the jobs first.
              </p>
            ) : (
              <p class="small muted" style="margin-bottom:14px;">
                It comes off the list and stays in every backup. Nothing is destroyed.
              </p>
            )}
            <div style="display:flex;gap:8px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={() => setConfirming(null)}>Keep it</button>
              <button class="btn btn-primary" disabled={busy || jobCount(confirming.id) > 0}
                onClick={() => void remove(confirming!)}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
